<template>
  <base-stack class="comments">
    <!-- Comment Blocking Section -->
    <div class="box">
      <base-stack direction="column">
        <p>Block this user from publishing chat comments.</p>

        <base-toggle :value="toggleValue" :disabled="loading" @input="handleToggleChange">
          {{ isCommentsBlocked ? 'Commenting blocked' : 'Commenting allowed' }}
          <span class="comments__blocked-since" v-if="isCommentsBlocked && user.commentsBlockedAt">
            (since {{ user.commentsBlockedAt | dayjs('MMM DD, YYYY (HH:mm)') }})
          </span>
        </base-toggle>
      </base-stack>
    </div>

    <base-stack as="ul">
      <app-user-comment-cell v-for="comment in user.comments" :key="comment.id" :comment="comment" />
    </base-stack>

    <p class="comments__empty" v-if="user.comments.length === 0">No comments</p>
  </base-stack>
</template>

<script lang="ts">
import AppUserCommentCell from '@/components/UserCommentCell.vue';
import { User } from '@/types/api';
import gql from 'graphql-tag';
import { Component, Prop, Vue } from 'vue-property-decorator';

@Component({
  components: { AppUserCommentCell },
})
export default class UserComments extends Vue {
  @Prop({ required: true }) user!: User;

  loading = false;

  get isCommentsBlocked(): boolean {
    return !!this.user.commentsBlockedAt;
  }

  get toggleValue(): boolean {
    return this.isCommentsBlocked;
  }

  async handleToggleChange(blocked: boolean) {
    if (blocked) {
      const removeExisting =
        this.user.comments.filter((c) => !c.deletedAt).length > 0
          ? await this.$modal.confirm({
              title: 'Remove existing comments',
              message: 'Do you also want to remove all existing comments from this user?',
              cancelButtonText: 'No, keep them',
              confirmButtonText: 'Yes, remove them',
            })
          : false;

      await this.updateCommentBlock(blocked, removeExisting);
    } else {
      await this.updateCommentBlock(blocked);
    }
  }

  async updateCommentBlock(blocked: boolean, removeExistingComments = false) {
    try {
      this.loading = true;
      await this.$apollo.mutate({
        mutation: gql`
          mutation BlockUserComments($input: BlockUserCommentsInput!) {
            blockUserComments(input: $input) {
              user {
                id
                commentsBlockedAt
              }
            }
          }
        `,
        variables: {
          input: {
            userId: this.user.id,
            blocked,
            removeExistingComments,
          },
        },
        refetchQueries: ['User'],
        awaitRefetchQueries: true,
      });
    } finally {
      this.loading = false;
    }
  }
}
</script>

<style lang="scss" scoped>
.box {
  margin: 16px 0;
  padding: 16px;
  border: 1px solid $color-theme-red;

  h3 {
    font-weight: bold;
  }
}

.loading {
  color: #666;
  font-style: italic;
}

.comments {
  display: flex;
  flex-direction: column;
  gap: 16px;

  ul {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &__empty {
    font-style: italic;
  }

  &__blocked-since {
    font-size: 0.8em;
    color: $color-theme-red;
  }
}
</style>
